import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth.js";
import { ApiError } from "../lib/api.js";
import {
  APP_DOWNLOAD_URL,
  APP_DOWNLOAD_NAME,
  LINUX_APPIMAGE_URL,
  LINUX_APPIMAGE_NAME,
  LINUX_DEB_URL,
  LINUX_DEB_NAME,
  detectPlatform,
} from "../lib/app.js";

type Mode = "login" | "register";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const plat = detectPlatform();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") {
        await auth.login(email, password);
      } else {
        await auth.register(email, password, displayName);
      }
      // Return the user to wherever RequireAuth bounced them from (e.g. a
      // /connect?id=<code> deep link from the desktop app), preserving the
      // pre-filled partner ID through the login gate; fall back to the device list.
      const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
      const dest = from?.pathname ? `${from.pathname}${from.search ?? ""}` : "/devices";
      navigate(dest, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const friendly: Record<string, string> = {
          invalid_credentials: "Wrong email or password.",
          account_disabled: "This account is disabled or was removed. Contact your administrator.",
          account_removed:
            "This account was removed by an administrator, so the email can't sign up again. Ask an admin to restore it.",
          email_in_use: "An account with this email already exists — sign in instead.",
          email_domain_not_allowed: "Sign-up is restricted to your organisation's email domain."
        };
        setError(friendly[err.code] ?? err.code);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
      <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-5 lg:gap-8 items-center">
        {/* Left column — brand + auth */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          {/* Header */}
          <div className="text-center lg:text-left mb-5">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-1.5">
              <img src="/logo.png" alt="SPIDER Viewer" className="w-12 h-12" />
              <h1 className="text-2xl font-bold text-white">SPIDER® Viewer</h1>
            </div>
            <p className="text-white/50 text-sm">Secure remote access to your devices</p>
          </div>

          {/* Card */}
          <div className="glass p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-white mb-0.5">
                {mode === "login" ? "Welcome back" : "Get started"}
              </h2>
              <p className="text-sm text-white/60">
                {mode === "login"
                  ? "Sign in to access your devices"
                  : "Create an account to manage your devices"}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3.5">
              {mode === "register" && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1.5">
                    Display name
                  </label>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg outline-none focus:border-blue-500 focus:bg-white/10 text-white placeholder:text-white/40 transition"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg outline-none focus:border-blue-500 focus:bg-white/10 text-white placeholder:text-white/40 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "Min. 12 characters" : "••••••••"}
                  required
                  minLength={mode === "register" ? 12 : 1}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg outline-none focus:border-blue-500 focus:bg-white/10 text-white placeholder:text-white/40 transition"
                />
                {mode === "register" && (
                  <p className="text-xs text-white/50 mt-1.5">
                    Use uppercase, lowercase, numbers, and symbols for security
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className={`w-full py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                  busy
                    ? "bg-white/5 text-white/50 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {busy ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Please wait…
                  </>
                ) : mode === "login" ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v2a2 2 0 01-2 2H7a2 2 0 01-2-2v-2m14-4V7a2 2 0 00-2-2H9a2 2 0 00-2 2v2" />
                    </svg>
                    Sign in
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Create account
                  </>
                )}
              </button>
            </form>

            {/* Toggle Mode — inline to save vertical space */}
            <div className="pt-3 border-t border-white/10 text-center text-sm">
              <span className="text-white/60">
                {mode === "login" ? "Don't have an account? " : "Already have an account? "}
              </span>
              <button
                type="button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="font-medium text-blue-400 hover:text-blue-300 transition"
              >
                {mode === "login" ? "Create one" : "Sign in"}
              </button>
            </div>
          </div>
        </div>

        {/* Right column — get the app + trust footer. Windows has the native
            host+viewer app; macOS & Linux use the browser as a viewer today. */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          <div className="glass p-4">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-sm font-semibold text-white">Get SPIDER Viewer</span>
              <span className="text-[11px] text-white/40">host &amp; viewer</span>
            </div>
            <div className="flex flex-col gap-2">
              {/* Windows — native download */}
              <a
                href={APP_DOWNLOAD_URL}
                download={APP_DOWNLOAD_NAME}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 bg-white/5 hover:bg-white/10 transition group ${
                  plat === "windows" ? "ring-1 ring-blue-400/50" : ""
                }`}
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-600/20 text-blue-300 shrink-0">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5.5 10.2 4.5v6.7H3V5.5Zm0 13L10.2 19.5v-6.6H3v5.6ZM11.2 4.35 21 3v8.2h-9.8V4.35Zm0 15.3L21 21v-8.1h-9.8v6.75Z" /></svg>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-white">Windows</span>
                  <span className="block text-xs text-white/50">Download setup · 64-bit · host &amp; viewer</span>
                </span>
                <svg className="w-4 h-4 text-blue-300/80 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                </svg>
              </a>

              {/* macOS — browser viewer for now */}
              <div className={`flex items-center gap-3 rounded-lg px-3 py-2 bg-white/[0.03] ${plat === "macos" ? "ring-1 ring-white/25" : ""}`}>
                <span className="flex items-center justify-center w-8 h-8 rounded-md bg-white/10 text-white/70 shrink-0">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3-.76.88-2 .87-2.35 1.53.06-1.13.5-2.16 1.2-2.95.75-.85 2.05-1.5 2.27-1.58ZM19.6 17.2c-.5 1.15-.74 1.66-1.38 2.68-.9 1.42-2.16 3.18-3.73 3.19-1.4.02-1.76-.9-3.66-.9-1.9.01-2.3.92-3.7.9-1.57-.02-2.77-1.6-3.66-3.02-2.5-3.98-2.76-8.65-1.22-11.14 1.1-1.77 2.83-2.8 4.46-2.8 1.66 0 2.7.9 4.07.9 1.33 0 2.14-.9 4.06-.9 1.46 0 3 .8 4.1 2.16-3.6 1.98-3 7.13.36 8.93Z" /></svg>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-white">macOS</span>
                  <span className="block text-xs text-white/50">Run in this browser — no install</span>
                </span>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/10 text-white/60 shrink-0">Browser</span>
              </div>

              {/* Linux — native download (AppImage default, .deb alternative).
                  Two sibling anchors in one row — never nest <a> inside <a>. */}
              <div className={`flex items-center gap-3 rounded-lg px-3 py-2 bg-white/5 ${plat === "linux" ? "ring-1 ring-blue-400/50" : ""}`}>
                <span className="flex items-center justify-center w-8 h-8 rounded-md bg-amber-500/20 text-amber-300 shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a.75.75 0 0 1-.75.75H3.75A.75.75 0 0 1 3 12V5.25" /></svg>
                </span>
                <a
                  href={LINUX_APPIMAGE_URL}
                  download={LINUX_APPIMAGE_NAME}
                  className="flex items-center gap-3 flex-1 min-w-0 group -my-2 py-2 rounded-lg hover:bg-white/[0.04] transition"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-white">Linux</span>
                    <span className="block text-xs text-white/50">Download AppImage · 64-bit</span>
                  </span>
                  <svg className="w-4 h-4 text-blue-300/80 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                  </svg>
                </a>
                <a
                  href={LINUX_DEB_URL}
                  download={LINUX_DEB_NAME}
                  title="Debian / Ubuntu package"
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/10 text-white/60 hover:bg-white/20 hover:text-white/90 shrink-0 transition"
                >
                  .deb
                </a>
              </div>
            </div>
            <p className="text-[11px] text-white/40 mt-2.5 leading-snug">
              On macOS, just sign in — the viewer runs in your browser. The installable host app
              (to make a machine remotely controllable) is ready for Windows and Linux.
            </p>
          </div>

          {/* Footer */}
          <p className="text-center lg:text-left text-xs text-white/40 mt-4">
            End-to-end encrypted • Secure device pairing • Zero-knowledge architecture
          </p>
        </div>
      </div>
    </div>
  );
}
